import {FormActions, FormLayout, SelectField, TextField} from '@hconnect/common/components'
import {yupResolver} from '@hookform/resolvers/yup'
import {isUndefined} from 'lodash'
import {useMemo} from 'react'
import {FormProvider, SubmitHandler, useForm, useFormState} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useTaskUpmPlantsOptions} from '../../hooks'
import {DALOG_ACTION_STATUSES, DalogActionFormValues, TaskSource, TaskStatus} from '../../types'
import {getDalogActionValidationSchema} from '../../validators'

import {DalogPriorityField} from './DalogPriorityField'
import {DalogUserSearchFormField} from './DalogUserSearchFormField'
import {EquipmentSearchField} from './EquipmentSearchField'
import {MainEquipmentSearchField} from './MainEquipmentSearchField'
import {ProcessStageField} from './ProcessStageField'
import {MomentDatePickerFormField, RichTextFormField} from './shared'
import {TaskStatusField} from './TaskStatusField'
import {TaskTypeField} from './TaskTypeField'

type Props = {
  onSubmit: SubmitHandler<DalogActionFormValues>
  onCancel: () => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  initialValues?: DalogActionFormValues
  externalTaskId?: string
  timezone: string
  plantId: string
  onStatusChange?: (newValue: TaskStatus | null, resetStatus: () => void) => void
}

const defaultValues: Partial<DalogActionFormValues> = {
  title: '',
  description: '',
  plantUpmId: undefined,
  status: TaskStatus.Pending,
  assignees: []
}

export const DalogActionForm = ({initialValues, timezone, plantId, ...props}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const validationSchema = useMemo(() => getDalogActionValidationSchema({t}), [t])

  const methods = useForm<DalogActionFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    values: initialValues,
    ...(!initialValues && {defaultValues})
  })

  const mainEquipment = methods.watch('mainEquipment')

  const {isValid, isSubmitting} = useFormState({
    control: methods.control
  })

  const plantOptions = useTaskUpmPlantsOptions(TaskSource.DALOG_ACTIONS)
  const plantUpmId = methods.watch('plantUpmId')

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(props.onSubmit)}>
        <FormLayout>
          <TaskTypeField value={TaskSource.DALOG_ACTIONS} onChange={props.onTaskTypeChange} />
          <TextField name="title" label={t('task.label.title')} control={methods.control} />
          <RichTextFormField
            name="description"
            label={t('task.label.description')}
            pressAndHoldRecordLabel={t('task.label.pressAndHoldRecord')}
            control={methods.control}
          />
          {timezone && (
            <MomentDatePickerFormField
              name="dueDate"
              disableDateSwitch={true}
              label={t('task.label.dueDate')}
              control={methods.control}
              onChange={(date) => {
                if (date) {
                  methods.setValue('dueDate', date.toISOString(), {shouldValidate: true})
                }
              }}
              timezone={timezone}
            />
          )}
          <SelectField
            disabled={true}
            name="plantUpmId"
            label={t('task.label.plant')}
            control={methods.control}
            options={plantOptions}
          />
          <TaskStatusField
            name="status"
            label={t('task.label.status')}
            control={methods.control}
            allowedStatuses={DALOG_ACTION_STATUSES}
            onChange={(newValue, previousValue) => {
              props.onStatusChange?.(newValue ?? null, () => {
                methods.setValue('status', previousValue as DalogActionFormValues['status'])
              })
            }}
          />
          <DalogPriorityField
            name="priority"
            label={t('task.label.priority')}
            control={methods.control}
          />
          <EquipmentSearchField
            plantId={plantId}
            name="equipment"
            label={t('task.label.equipment')}
            control={methods.control}
            onChange={(equipment) => {
              const mainEquipmentData = isUndefined(equipment)
                ? undefined
                : (methods.getValues('mainEquipment') ?? equipment?.mainEquipment)
              methods.setValue('mainEquipment', mainEquipmentData)
              methods.setValue('processStage', equipment?.processStage?.processStep)
            }}
            mainEquipmentId={mainEquipment?.id}
            optional
          />
          <MainEquipmentSearchField
            plantId={plantId}
            name="mainEquipment"
            label={t('task.label.mainEquipment')}
            control={methods.control}
            optional
          />
          <ProcessStageField
            plantId={plantId}
            name="processStage"
            label={t('task.label.processStage')}
            control={methods.control}
            optional
          />
          <DalogUserSearchFormField
            plantUpmId={plantUpmId}
            name="assignees"
            label={t('task.label.assignee')}
            pleaseStartTypingLabel={t('task.label.pleaseStartTyping')}
            control={methods.control}
            optional
          />
          <FormActions onCancel={props.onCancel} isSubmitting={isSubmitting} isValid={isValid} />
        </FormLayout>
      </form>
    </FormProvider>
  )
}
